// Shared bits for the kiosk fleet pages (super admin): time + money formatting,
// the online dot, the pair dialog (with the tenant link picker), the tenant
// select, the court-link cell, the staff-PIN status and a one-field prompt
// modal. Styled like SportsVouchersPage / the super-admin pages.
import { useEffect, useState } from 'react';
import { X, Smartphone, Copy, RotateCcw, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useBackdropClose } from '../utils/backdropClose';
import { pairDevice, courtLinkUrl, errMsg, errDetail } from './kioskApi';

export const RED = '#E63946';
export const GREEN = '#00E676';
export const AMBER = '#FFB300';
export const GREY = '#737373';

export const inputStyle = {
  background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff',
  borderRadius: 8, padding: '10px 12px', width: '100%', outline: 'none',
  fontFamily: 'Manrope, sans-serif', fontSize: 14, boxSizing: 'border-box',
};

export const labelStyle = { fontSize: 11, textTransform: 'uppercase', color: GREY, letterSpacing: 1 };

export const iconBtn = {
  background: 'none', border: '1px solid #2A2A2A', borderRadius: 8, padding: 7, color: GREY, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
};

export function fmtMs(ms) {
  if (!ms) return '—';
  const d = new Date(ms);
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function fmtAgo(ms, now = Date.now()) {
  if (!ms) return 'never';
  const s = Math.max(0, Math.round((now - ms) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
}

export function fmtDuration(ms) {
  if (ms == null) return '';
  const m = Math.round(ms / 60000);
  if (m < 1) return '< 1 min';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h} h ${m % 60} min`;
}

export function fmtCentavos(c) {
  if (c == null || Number.isNaN(Number(c))) return '—';
  const n = Number(c) / 100;
  const s = Math.abs(n).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `−₱${s}` : `₱${s}`;
}

export function deviceState(d) {
  if (d.status === 'revoked') return { key: 'revoked', label: 'Revoked', color: GREY };
  if (d.is_online) {
    if (d.out_of_service) return { key: 'oos', label: 'Out of service', color: AMBER };
    return { key: 'online', label: 'Online', color: GREEN };
  }
  return { key: 'offline', label: 'Offline', color: '#FF2D55' };
}

export function OnlineDot({ device, size = 10 }) {
  const s = deviceState(device);
  return (
    <span data-testid={`kiosk-dot-${s.key}`} title={s.label}
      style={{ display: 'inline-block', width: size, height: size, borderRadius: 999, background: s.color,
        boxShadow: s.key === 'online' ? `0 0 8px ${GREEN}` : 'none', flexShrink: 0 }} />
  );
}

export function StateBadge({ device }) {
  const s = deviceState(device);
  return (
    <span data-testid={`kiosk-state-${s.key}`} style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5,
      color: s.color, background: s.color + '18', padding: '3px 8px', borderRadius: 999 }}>{s.label}</span>
  );
}

export const copyText = (text, what) => {
  if (!text) return;
  navigator.clipboard?.writeText(text);
  toast.success(`Copied ${what}`);
};

// Tenant link picker: '' = no tenant (a court without an account, served by the
// public link). `tenants` comes from GET /tenants.
export function TenantSelect({ value, tenants = [], onChange, disabled = false, testId = 'kiosk-tenant-select', compact = false }) {
  return (
    <select value={value || ''} disabled={disabled} data-testid={testId}
      onChange={e => onChange(e.target.value || null)}
      style={{ ...inputStyle, cursor: disabled ? 'not-allowed' : 'pointer', padding: compact ? '6px 8px' : inputStyle.padding,
        fontSize: compact ? 12 : 14, width: compact ? 200 : '100%', opacity: disabled ? 0.6 : 1 }}>
      <option value="">No tenant — public link only</option>
      {tenants.map(t => (
        <option key={t.org_id} value={t.org_id}>{t.name}{t.slug ? ` (${t.slug})` : ''}</option>
      ))}
    </select>
  );
}

// The court's permanent /m/{code} link: code, copy, open, rotate (rotate is
// confirmed by the parent — it breaks the link the court already has).
export function CourtLinkCell({ device, onRotate, compact = true }) {
  const link = device.court_link;
  if (!link?.code) return <span style={{ color: '#555', fontSize: 12 }}>assigning…</span>;
  const url = courtLinkUrl(link);
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={`kiosk-court-link-${device.kiosk_id}`}>
      <code style={{ fontFamily: 'Oswald, sans-serif', fontSize: compact ? 14 : 18, letterSpacing: 2, color: '#fff',
        background: '#0A0A0A', border: '1px solid #2A2A2A', borderRadius: 6, padding: compact ? '3px 8px' : '6px 12px' }}>
        {link.code}
      </code>
      <button onClick={() => copyText(url, 'the court link')} title={url} data-testid={`kiosk-court-link-copy-${device.kiosk_id}`} style={iconBtn}>
        <Copy className="h-4 w-4" />
      </button>
      <a href={url} target="_blank" rel="noreferrer" title="Open the court page" data-testid={`kiosk-court-link-open-${device.kiosk_id}`} style={{ ...iconBtn, textDecoration: 'none' }}>
        <ExternalLink className="h-4 w-4" />
      </a>
      {device.status === 'active' && onRotate && (
        <button onClick={() => onRotate(device)} title="Rotate (replace) this link" data-testid={`kiosk-court-link-rotate-${device.kiosk_id}`}
          style={{ ...iconBtn, color: AMBER, borderColor: AMBER + '40' }}>
          <RotateCcw className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

// What the server holds of the phone's staff PIN — the verifier's version, or nothing yet.
export function StaffPinStatus({ device, now = Date.now() }) {
  const locked = device.court_pin_locked_until_ms && device.court_pin_locked_until_ms > now;
  if (!device.staff_pin_present) {
    return <span data-testid={`kiosk-pin-${device.kiosk_id}`} title="The phone has not shared its staff PIN yet — set it on the kiosk and let it heartbeat once"
      style={{ fontSize: 12, color: AMBER, fontWeight: 700 }}>Not set</span>;
  }
  return (
    <span data-testid={`kiosk-pin-${device.kiosk_id}`} style={{ fontSize: 12, color: GREEN, fontWeight: 700 }}>
      v{device.staff_pin_version}
      {locked && <span title={`Court link locked until ${fmtMs(device.court_pin_locked_until_ms)}`} style={{ color: '#FF2D55', marginLeft: 6 }}>LOCKED</span>}
    </span>
  );
}

export const primaryBtn = (disabled) => ({
  flex: 2, background: RED, color: '#fff', border: 'none', borderRadius: 10, padding: 11, fontWeight: 700,
  textTransform: 'uppercase', fontFamily: 'Oswald, sans-serif', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
});
export const ghostBtn = {
  flex: 1, background: 'transparent', color: '#a3a3a3', border: '1px solid #2A2A2A', borderRadius: 10, padding: 11, cursor: 'pointer',
};

export function ModalShell({ title, onClose, children, testId }) {
  const backdrop = useBackdropClose(onClose);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }} {...backdrop} data-testid={testId}>
      <div style={{ background: '#141414', border: '1px solid #1E1E1E', borderRadius: 16, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between" style={{ padding: '18px 20px', borderBottom: '1px solid #1E1E1E' }}>
          <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 18, fontWeight: 800, textTransform: 'uppercase' }}>{title}</h2>
          <button onClick={onClose} data-testid={`${testId}-close`} style={{ background: 'none', border: 'none', color: GREY, cursor: 'pointer' }}><X className="h-5 w-5" /></button>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}

// Pair a phone: the owner types the 6-character code the phone shows.
// `replace` = an existing device record to re-pair (token rotates, history and
// court link stay). `tenants` feeds the optional link picker.
export function PairDeviceDialog({ replace = null, tenants = [], onClose, onDone }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState(replace?.name || '');
  const [site, setSite] = useState(replace?.site_name || '');
  const [orgId, setOrgId] = useState(replace?.court_org_id || '');
  const [saving, setSaving] = useState(false);
  const [ownerKey, setOwnerKey] = useState('');          // 7.0e(g): re-pairing asks the owner PIN or authenticator code
  const clean = code.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
  const canSubmit = clean.length === 6 && name.trim().length > 0 && !saving && (!replace || ownerKey.length >= 4);

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const body = { code: clean, name: name.trim(), site_name: site.trim() };
      if (orgId) body.court_org_id = orgId;
      if (replace?.kiosk_id) { body.replace_kiosk_id = replace.kiosk_id; body.owner_code = ownerKey; }
      const r = await pairDevice(body);
      toast.success(replace ? `Re-paired ${name.trim()}` : `Paired ${name.trim()}`);
      onDone?.(r);
    } catch (e) { const dd = errDetail(e); toast.error((dd && dd.message) || errMsg(e, 'Pairing failed')); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title={replace ? 'Pair this device again' : 'Pair a device'} onClose={onClose} testId="kiosk-pair-dialog">
      <div className="flex items-start gap-3 mb-4" style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', borderRadius: 10, padding: 12 }}>
        <Smartphone className="h-5 w-5 shrink-0" style={{ color: RED }} />
        <p style={{ fontSize: 12, color: '#a3a3a3', lineHeight: 1.5 }}>
          On the phone, open Settings → Fleet → Pair. It shows a 6-character code that lasts 5 minutes. Type it here.
          {replace && <> This keeps <b style={{ color: '#fff' }}>{replace.name}</b>&apos;s history and court link and issues the phone a new key.</>}
        </p>
      </div>
      {replace && (
        <label style={{ display: 'block', marginBottom: 14 }}>
          <span style={labelStyle}>Owner PIN or authenticator code</span>
          <input data-testid="kiosk-pair-owner-key" value={ownerKey} onChange={e => setOwnerKey(e.target.value.replace(/\D/g, '').slice(0, 8))} inputMode="numeric" type="password" placeholder="Confirms it is you"
            style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', borderRadius: 8, padding: '9px 12px', width: '100%', outline: 'none', fontSize: 14, letterSpacing: 3, marginTop: 6 }} />
        </label>
      )}
      <label style={{ display: 'block', marginBottom: 14 }}>
        <span style={labelStyle}>Code from the phone</span>
        <input data-testid="kiosk-pair-code" value={clean} onChange={e => setCode(e.target.value)} placeholder="ABC234" autoFocus
          onKeyDown={e => { if (e.key === 'Enter') submit(); }}
          style={{ ...inputStyle, marginTop: 6, fontFamily: 'Oswald, sans-serif', fontSize: 24, letterSpacing: 6, textAlign: 'center', textTransform: 'uppercase' }} />
      </label>
      <label style={{ display: 'block', marginBottom: 14 }}>
        <span style={labelStyle}>Device name *</span>
        <input data-testid="kiosk-pair-name" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Court 2 phone" maxLength={60}
          style={{ ...inputStyle, marginTop: 6 }} />
      </label>
      <label style={{ display: 'block', marginBottom: 14 }}>
        <span style={labelStyle}>Site</span>
        <input data-testid="kiosk-pair-site" value={site} onChange={e => setSite(e.target.value)} placeholder="e.g. Riverside Court" maxLength={60}
          style={{ ...inputStyle, marginTop: 6 }} />
        <span style={{ fontSize: 11, color: '#555' }}>Shown on the phone and used to group sales.</span>
      </label>
      <label style={{ display: 'block' }}>
        <span style={labelStyle}>Tenant (court with an account)</span>
        <div style={{ marginTop: 6 }}>
          <TenantSelect value={orgId} tenants={tenants} onChange={v => setOrgId(v || '')} testId="kiosk-pair-tenant" />
        </div>
        <span style={{ fontSize: 11, color: '#555' }}>Optional. Every row this phone sends is stamped with the tenant linked at the time — you can link or unlink later.</span>
      </label>
      <div className="flex gap-3 mt-6">
        <button onClick={onClose} data-testid="kiosk-pair-cancel" style={ghostBtn}>Cancel</button>
        <button onClick={submit} disabled={!canSubmit} data-testid="kiosk-pair-submit" style={primaryBtn(!canSubmit)}>
          {saving ? 'Pairing…' : replace ? 'Pair again' : 'Pair device'}
        </button>
      </div>
    </ModalShell>
  );
}

// One text field with a Save button — rename, set site, edit notes.
export function TextPromptModal({ title, label, initial = '', placeholder = '', maxLength = 60, multiline = false, required = false, onSave, onClose, testId = 'kiosk-prompt' }) {
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setValue(initial); }, [initial]);
  const can = (!required || value.trim().length > 0) && !saving;
  const save = async () => {
    if (!can) return;
    setSaving(true);
    try { await onSave(value.trim()); onClose(); }
    catch (e) { toast.error(errMsg(e, 'Could not save')); }
    finally { setSaving(false); }
  };
  return (
    <ModalShell title={title} onClose={onClose} testId={testId}>
      <label style={{ display: 'block' }}>
        <span style={labelStyle}>{label}</span>
        {multiline ? (
          <textarea data-testid={`${testId}-input`} value={value} onChange={e => setValue(e.target.value)} placeholder={placeholder} maxLength={maxLength} rows={4}
            style={{ ...inputStyle, marginTop: 6, resize: 'vertical' }} />
        ) : (
          <input data-testid={`${testId}-input`} value={value} onChange={e => setValue(e.target.value)} placeholder={placeholder} maxLength={maxLength} autoFocus
            onKeyDown={e => { if (e.key === 'Enter') save(); }}
            style={{ ...inputStyle, marginTop: 6 }} />
        )}
      </label>
      <div className="flex gap-3 mt-6">
        <button onClick={onClose} data-testid={`${testId}-cancel`} style={ghostBtn}>Cancel</button>
        <button onClick={save} disabled={!can} data-testid={`${testId}-save`} style={primaryBtn(!can)}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </ModalShell>
  );
}

// Shown when the retail token is missing / not a super admin.
export function AdminDeniedNotice() {
  return (
    <div data-testid="kiosk-admin-denied" style={{ textAlign: 'center', padding: 60, background: '#141414', borderRadius: 12, border: '1px solid #1E1E1E' }}>
      <Smartphone className="h-10 w-10 mx-auto mb-3" style={{ color: '#333' }} />
      <p style={{ color: '#fff', fontSize: 15, fontWeight: 700 }}>Super admin access required</p>
      <p style={{ color: GREY, fontSize: 13, marginTop: 6 }}>Sign in at /admin with the super admin account (authenticator code), then come back.</p>
    </div>
  );
}

// In-sync indicator (K6 P2): the phone's own totals from its last heartbeat vs
// the rows the server holds. Nothing about money — counts only.
export function SyncBadge({ state, testId = 'kiosk-sync' }) {
  if (!state || !state.known) return <span data-testid={testId} style={{ fontSize: 11, color: '#555' }}>not reported</span>;
  if (state.in_sync) return <span data-testid={testId} style={{ fontSize: 11, fontWeight: 800, color: GREEN }}>IN SYNC</span>;
  const bits = [];
  if (state.sessions_behind) bits.push(`${state.sessions_behind} session${state.sessions_behind === 1 ? '' : 's'} behind`);
  if (state.collections_behind) bits.push(`${state.collections_behind} settlement${state.collections_behind === 1 ? '' : 's'} behind`);
  if (state.mirror_extra) bits.push(`${state.mirror_extra} row${state.mirror_extra === 1 ? '' : 's'} only on the server`);
  if (!bits.length && state.gross_diff_centavos) bits.push(`gross differs by ${fmtCentavos(state.gross_diff_centavos)}`);
  return (
    <span data-testid={testId} title={`Phone: ${state.phone?.sessions} sessions · ${state.phone?.collections} settlements · gross ${fmtCentavos(state.phone?.gross_centavos)}\nServer: ${state.mirror?.sessions} · ${state.mirror?.collections} · ${fmtCentavos(state.mirror?.gross_centavos)}`}
      style={{ fontSize: 11, fontWeight: 700, color: state.mirror_extra ? '#FF2D55' : AMBER }}>
      {bits.join(' · ') || 'behind'}
    </span>
  );
}
